<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['invite']);

const { t } = useI18n();

const dialogRef = ref(null);
const name = ref('');
const emailAddress = ref('');
const role = ref('agent');

const v$ = useVuelidate(
  {
    name: { required },
    emailAddress: { required, email },
  },
  { name, emailAddress }
);

const roleOptions = computed(() => [
  { value: 'agent', label: t('TEAM_PAGE.ROLES.AGENT') },
  { value: 'administrator', label: t('TEAM_PAGE.ROLES.ADMINISTRATOR') },
]);

const resetForm = () => {
  name.value = '';
  emailAddress.value = '';
  role.value = 'agent';
  v$.value.$reset();
};

const open = () => dialogRef.value?.open();

const onSuccess = () => {
  resetForm();
  dialogRef.value?.close();
};

const handleConfirm = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('invite', {
    name: name.value.trim(),
    email: emailAddress.value.trim(),
    role: role.value,
  });
};

defineExpose({ open, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('TEAM_PAGE.INVITE_DIALOG.TITLE')"
    :description="t('TEAM_PAGE.INVITE_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('TEAM_PAGE.INVITE_DIALOG.SUBMIT')"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('TEAM_PAGE.INVITE_DIALOG.NAME_LABEL')"
        :placeholder="t('TEAM_PAGE.INVITE_DIALOG.NAME_PLACEHOLDER')"
        :disabled="isLoading"
        :message="v$.name.$error ? t('TEAM_PAGE.INVITE_DIALOG.NAME_ERROR') : ''"
        :message-type="v$.name.$error ? 'error' : 'info'"
        autofocus
        @input="v$.name.$touch"
      />
      <Input
        v-model="emailAddress"
        type="email"
        :label="t('TEAM_PAGE.INVITE_DIALOG.EMAIL_LABEL')"
        :placeholder="t('TEAM_PAGE.INVITE_DIALOG.EMAIL_PLACEHOLDER')"
        :disabled="isLoading"
        :message="
          v$.emailAddress.$error ? t('TEAM_PAGE.INVITE_DIALOG.EMAIL_ERROR') : ''
        "
        :message-type="v$.emailAddress.$error ? 'error' : 'info'"
        @input="v$.emailAddress.$touch"
      />
      <label
        class="flex flex-col gap-1 mb-0 text-sm font-medium text-n-slate-12"
      >
        {{ t('TEAM_PAGE.INVITE_DIALOG.ROLE_LABEL') }}
        <Select
          v-model="role"
          :options="roleOptions"
          :disabled="isLoading"
          class="!w-full [&>select]:w-full"
        />
      </label>
    </div>
  </Dialog>
</template>
